import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealReviewModal from "../../components/DealReviewModal";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { FileText, Send, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "Pending Review",
  active: "Live",
  approved: "Approved",
  reserved: "Under Contract",
  in_closing: "In Closing",
  closed: "Closed",
  rejected: "Rejected",
  changes_requested: "Changes Requested",
  removed: "Removed",
};

const STATUS_COLORS = {
  pending: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  active: "bg-green-500/10 text-green-500 border-green-500/20",
  approved: "bg-green-500/10 text-green-500 border-green-500/20",
  reserved: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  in_closing: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  closed: "bg-gray-500/10 text-gray-500 border-gray-500/20",
  rejected: "bg-red-500/10 text-red-500 border-red-500/20",
  changes_requested: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  removed: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

const DocumentOversightPage = () => {
  const [signingStatus, setSigningStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSigningStatus = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/deals/signing-status`, { 
        withCredentials: true, 
        headers 
      });
      setSigningStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch signing status:", error);
      toast.error("Failed to fetch document status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSigningStatus();
  }, []);

  const handleSendReminder = async (dealId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin/deals/${dealId}/send-reminder`,
        {},
        { withCredentials: true, headers }
      );
      toast.success("Reminder sent to investor");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleReviewDeal = (deal) => {
    setSelectedDeal(deal);
    setShowReviewModal(true);
  };

  const handleDealUpdated = () => {
    fetchSigningStatus();
    setShowReviewModal(false);
    setSelectedDeal(null);
  };

  return (
    <DashboardLayout title="Document Oversight">
      <div className="space-y-6 animate-fade-in" data-testid="document-oversight-page">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Signing Oversight
              <Badge variant="outline">{signingStatus.length} Active Deals</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="loading-spinner border-blue-500"></div>
              </div>
            ) : signingStatus.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No deals pending document signing</p>
              </div>
            ) : (
              <div className="space-y-4">
                {signingStatus.map((deal) => (
                  <Card key={deal.deal_id} className={`border ${deal.completion_percent < 50 ? 'border-orange-500/50' : ''}`}>
                    <CardContent className="pt-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium">{deal.property_address}</h4>
                            <Badge variant="outline" className={STATUS_COLORS[deal.status]}>
                              {STATUS_LABELS[deal.status]}
                            </Badge>
                            {deal.days_since_reserved > 7 && (
                              <Badge className="bg-orange-500 text-white">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {deal.days_since_reserved} days
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Investor: {deal.investor?.name || "Unknown"} ({deal.investor?.email})
                          </p>
                          
                          {/* Progress */}
                          <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm">Progress: {deal.signed_count}/{deal.total_docs} documents</span>
                              <span className="text-sm font-medium">{deal.completion_percent}%</span>
                            </div>
                            <Progress value={deal.completion_percent} className="h-2" />
                          </div>
                          
                          {/* Blocking Documents */}
                          {deal.blocking_documents && deal.blocking_documents.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-muted-foreground">Pending:</span>
                              {deal.blocking_documents.map((doc) => (
                                <Badge key={doc} variant="outline" className="text-xs bg-red-500/10 text-red-500">
                                  {doc.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSendReminder(deal.deal_id)}
                            data-testid={`send-reminder-${deal.deal_id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send Reminder
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleReviewDeal(deal)}
                            data-testid={`view-deal-${deal.deal_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deal Review Modal */}
      <DealReviewModal
        deal={selectedDeal}
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        onDealUpdated={handleDealUpdated}
      />
    </DashboardLayout>
  );
};

export default DocumentOversightPage;
